import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getShopById, getServicesByShopId, getReviewCountByShopId, getReviewsByShopId } from '../supabase/queries';
import { Shop, Service, Review } from '../supabase/queries';
// Removed Header and Footer imports as they are not part of the new design
// import Header from '../components/Header';
// import Footer from '../components/Footer';

const ShopDetailPage: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const [shop, setShop] = useState<Shop | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]); // State for reviews
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'services' | 'reviews'>('services'); // State for tabs

  useEffect(() => {
    console.log('ShopDetailPage: shopId from useParams:', shopId);
    const fetchData = async () => {
      if (!shopId) {
        console.log('ShopDetailPage: Shop ID is missing, setting error.');
        setError('Shop ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const shopData = await getShopById(shopId);
        if (shopData) {
          const reviewCount = await getReviewCountByShopId(shopId);
          setShop({ ...shopData, review_count: reviewCount });
          const servicesData = await getServicesByShopId(shopId);
          setServices(servicesData || []);
          const reviewsData = await getReviewsByShopId(shopId);
          setReviews(reviewsData || []);
        } else {
          setError('Shop not found.');
        }
      } catch (err) {
        console.error('Error fetching shop details:', err);
        setError('Failed to load shop details.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shopId]);

  if (loading) {
    console.log('ShopDetailPage: Loading state active.');
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white">
        Loading shop details...
      </div>
    );
  }

  if (error) {
    console.log('ShopDetailPage: Error state active, error:', error);
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!shop) {
    console.log('ShopDetailPage: No shop data available.');
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-gray-400">
        No shop data available.
      </div>
    );
  }

  console.log('ShopDetailPage: Shop data loaded:', shop);
  console.log('ShopDetailPage: Services data loaded:', services);

  const calculatedAverageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header Section */}
      <div
        className="relative w-full h-64 bg-cover bg-center flex flex-col justify-between p-6"
        style={{ backgroundImage: `url(${shop.image_url || 'https://via.placeholder.com/900x600?text=Shop+Image'})` }}
      >
        <div className="absolute inset-0 bg-teal-700 opacity-50"></div> {/* Teal background */}
        <div className="relative z-10 flex justify-between items-center w-full">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-800 bg-opacity-50 hover:bg-opacity-75 transition">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h17" />
            </svg>
          </button>
          <button className="p-2 rounded-full bg-gray-800 bg-opacity-50 hover:bg-opacity-75 transition">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-white">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.815 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </button>
        </div>
       
      </div>

      {/* Main Content Area */}
      <div className="relative bg-gray-900 rounded-t-3xl -mt-8 p-6 z-20 flex-grow overflow-y-auto">
        {/* Doctor/Shop Details */}
        <h2 className="text-3xl font-bold text-white mb-2">{shop.name}</h2>
        <div className="flex items-center mb-4">
          <span className="text-yellow-400 text-xl mr-2">★</span>
          <span className="text-white text-lg mr-2">{calculatedAverageRating}</span>
          <span className="text-gray-400 text-lg">({shop.review_count ?? 0} Reviews)</span>
         
        </div>

        {/* Special Offer */}
        <div className="bg-teal-800 bg-opacity-50 rounded-lg p-4 mb-6 flex items-center">
          <span className="text-yellow-300 mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          <p className="text-white text-sm">Special Offer: 10% off for first-time patients this month.</p>
        </div>

        {/* About Section */}
        <h3 className="text-2xl font-bold text-white mb-3">About</h3>
        <p className="text-gray-400 mb-6">{shop.description}</p>

        {/* Services/Reviews Tabs */}
        <div className="flex border-b border-gray-700 mb-6">
          <button
            className={`py-2 px-4 text-lg font-semibold ${activeTab === 'services' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-gray-400 hover:text-white'}`}
            onClick={() => setActiveTab('services')}
          >
            Services
          </button>
          <button
            className={`py-2 px-4 text-lg font-semibold ${activeTab === 'reviews' ? 'text-teal-400 border-b-2 border-teal-400' : 'text-gray-400 hover:text-white'}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews ({shop.review_count ?? 0})
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'services' && (
          <div className="space-y-4 pb-20"> {/* Added padding-bottom to account for fixed button */}
            {services.length === 0 ? (
              <p className="text-gray-400">No services available for this shop.</p>
            ) : (
              services.map((service) => (
                <div key={service.id} className="flex justify-between items-center bg-gray-800 p-4 rounded-lg shadow-md">
                  <div>
                    <h4 className="text-xl font-semibold text-white">{service.name}</h4>
                    {/* <p className="text-gray-400 text-sm">{service.description}</p> */}
                  </div>
                  <p className="text-teal-400 font-bold text-lg">₹{service.price}</p>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-4 pb-20"> {/* Added padding-bottom to account for fixed button */}
            {reviews.length === 0 ? (
              <p className="text-gray-400">No reviews available for this shop.</p>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="bg-gray-800 p-4 rounded-lg shadow-md">
                  <div className="flex items-center mb-2">
                    <span className="text-yellow-400 text-lg mr-1">{'★'.repeat(review.rating)}</span>
                    <span className="text-gray-400 text-sm">({review.rating}/5)</span>
                  </div>
                  <p className="text-gray-500 text-xs">Anonymous User on {new Date(review.created_at).toLocaleDateString()}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Book Now Button */}
      <div className="bg-gray-900 p-4 shadow-lg z-30">
        <button
          onClick={() => navigate(`/book/${shopId}`)} // Example navigation to a booking page
          className="w-full py-3 bg-teal-600 text-white text-xl font-bold rounded-lg hover:bg-teal-700 transition"
        >
          Book Now (Starts at ₹{services.length > 0 ? Math.min(...services.map(s => s.price)) : 'N/A'})
        </button>
      </div>
    </div>
  );
};

export default ShopDetailPage;
